import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiGrid, FiList, FiFileText, FiTruck, FiColumns, FiDownload, FiFilter, FiCheck, FiTrash2 } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';
import { reportesService } from '../../services';
import { useProveedores } from '../../hooks/useProveedores/useProveedores';

const Proveedores = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Usar el hook personalizado para proveedores
  const {
    proveedores,
    isLoading,
    error,
    searchQuery,
    searchProveedores,
    deleteProveedor,
    getProveedoresStats,
    clearError
  } = useProveedores();

  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterEstado, setFilterEstado] = useState('ALL');
  const [filterPais, setFilterPais] = useState('ALL');
  const [filterCredito, setFilterCredito] = useState('ALL');
  const filterDropdownRef = useRef(null);

  // Wave SVG dinámica
  const menuRef    = useRef(null);
  const sec1Ref    = useRef(null);
  const sec2Ref    = useRef(null);
  const sec3Ref    = useRef(null);
  const sec4Ref    = useRef(null);
  const svgPathRef = useRef(null);

  const buildWavePath = useCallback(() => {
    if (!menuRef.current || !sec1Ref.current || !sec2Ref.current || !sec3Ref.current || !sec4Ref.current || !svgPathRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const W = menuRect.width;
    if (W === 0) return;
    const VB = 1000, TOP = 3, BOT = 53, half = 18, scale = VB / W;
    const b1 = (sec1Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const b2 = (sec2Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const b3 = (sec3Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const d = [
      `M 0,${TOP}`,
      `L ${b1 - half},${TOP}`,
      `C ${b1},${TOP} ${b1},${BOT} ${b1 + half},${BOT}`,
      `L ${b2 - half},${BOT}`,
      `C ${b2},${BOT} ${b2},${TOP} ${b2 + half},${TOP}`,
      `L ${b3 - half},${TOP}`,
      `C ${b3},${TOP} ${b3},${BOT} ${b3 + half},${BOT}`,
      `L ${VB},${BOT}`,
    ].join(' ');
    svgPathRef.current.setAttribute('d', d);
  }, []);

  useEffect(() => {
    if (!menuRef.current) return;
    buildWavePath();
    const t = setTimeout(buildWavePath, 100);
    const ro = new ResizeObserver(buildWavePath);
    [menuRef, sec1Ref, sec2Ref, sec3Ref, sec4Ref].forEach(r => { if (r.current) ro.observe(r.current); });
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [buildWavePath, isLoading]);

  useEffect(() => {
    const handler = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target))
        setShowFilterDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);


  const handleAddNew = () => {
    navigate('/system/proveedores/nuevo');
  };

  const handleDeleteProveedor = useCallback(async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('¿Desactivar este proveedor? Seguirá en la base de datos como inactivo.')) return;
    await deleteProveedor(id);
  }, [deleteProveedor]);

  const handleDownload = () => {
    setShowDownloadModal(true);
  };

  const handleDownloadReport = async ({ startDate, endDate, format }) => {
    setIsDownloading(true);

    try {
      // Llamar al servicio de reportes
      const params = {
        format: format, // csv o pdf
        date_from: startDate,
        date_to: endDate,
        is_active: true // Solo proveedores activos
      };

      const result = await reportesService.exportarProveedores(params);

      if (result.success) {
        // Determinar el tipo MIME según el formato
        const mimeTypes = {
          csv: 'text/csv',
          pdf: 'application/pdf',
          excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        };

        // Determinar la extensión del archivo
        const extensions = {
          csv: 'csv',
          pdf: 'pdf',
          excel: 'xlsx'
        };

        // Crear blob y descargar
        const blob = new Blob([result.data], {
          type: mimeTypes[format] || 'application/octet-stream'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Generar nombre de archivo apropiado
        const fileName = startDate && endDate
          ? `proveedores_${startDate}_${endDate}.${extensions[format]}`
          : `proveedores_todos_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Cerrar modal y mostrar mensaje de éxito
        setShowDownloadModal(false);
        alert(`Reporte descargado exitosamente en formato ${format.toUpperCase()}`);
      } else {
        throw new Error(result.message || 'Error al generar el reporte');
      }
    } catch (error) {
      console.error('Error al descargar reporte:', error);
      // Mostrar el motivo real (viene del servidor) para poder diagnosticar
      // fallos que solo ocurren en producción.
      const detalle = error.message && error.message !== 'Error al generar el reporte'
        ? `\n\nDetalle: ${error.message}`
        : '';
      alert(`Error al descargar el reporte. Por favor intente nuevamente.${detalle}`);
    } finally {
      setIsDownloading(false);
    }
  };

  // Manejar búsqueda (actualiza query, la búsqueda real se hace con debounce)
  const handleSearchChange = (e) => {
    searchProveedores(e.target.value);
  };

  // Limpiar búsqueda
  const handleClearSearch = () => {
    searchProveedores('');
  };

  // Limpiar errores
  const handleClearError = () => {
    clearError();
  };

  const getThumbnailType = (pais, estado) => {
    if (estado === 'INACTIVE') {
      return 'gray-gradient';
    }
    
    // Colores por país o región
    if (pais && pais.toLowerCase().includes('usa') || pais && pais.toLowerCase().includes('estados unidos')) {
      return 'blue-dark-gradient';
    } else if (pais && (pais.toLowerCase().includes('mexico') || pais.toLowerCase().includes('méxico'))) {
      return 'green-gradient';
    } else if (pais && pais.toLowerCase().includes('china')) {
      return 'red-gradient';
    } else if (pais && pais.toLowerCase().includes('india')) {
      return 'orange-gradient';
    } else {
      return 'purple-gradient';
    }
  };

  const getStatusLabel = (estado) => {
    switch (estado) {
      case 'ACTIVE': return 'Activo';
      case 'INACTIVE': return 'Inactivo';
      case 'PENDING': return 'Pendiente';
      case 'BLOCKED': return 'Bloqueado';
      default: return 'Desconocido';
    }
  };

  // Filtrado client-side
  const paisesUnicos = ['ALL', ...new Set(proveedores.map(p => p.pais).filter(Boolean))];

  const proveedoresFiltrados = proveedores.filter(p => {
    const okEstado  = filterEstado  === 'ALL' || p.estado === filterEstado;
    const okPais    = filterPais    === 'ALL' || p.pais   === filterPais;
    // "Con crédito" = tiene línea de crédito (monto) o días de crédito.
    // Antes solo miraba los días y un proveedor con límite pero sin días
    // caía en "sin crédito".
    const tieneCredito = p.limiteCredito > 0 || p.diasCredito > 0;
    const okCredito = filterCredito === 'ALL'
      || (filterCredito === 'CON_CREDITO'  && tieneCredito)
      || (filterCredito === 'SIN_CREDITO'  && !tieneCredito);
    return okEstado && okPais && okCredito;
  });

  const hayFiltrosActivos = filterEstado !== 'ALL' || filterPais !== 'ALL' || filterCredito !== 'ALL';

  if (isLoading) {
    return <LoadingGrid message="Cargando proveedores..." />;
  }

  return (
    <div className="proveedores-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiFileText className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button 
              className="btn btn--ghost btn--sm" 
              onClick={handleClearError}
              style={{ marginLeft: '10px' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}
      
      {/* Título */}
      <h1 className="page-title">PROVEEDORES</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu" ref={menuRef}>
          <svg className="action-bar-wave" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 56" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="waveGradProveedores" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%"   stopColor="#a64b95" stopOpacity="0.75" />
                <stop offset="45%"  stopColor="#c975b9" stopOpacity="0.75" />
                <stop offset="100%" stopColor="#7e2371" stopOpacity="0.75" />
              </linearGradient>
            </defs>
            <path ref={svgPathRef} d="" fill="none" stroke="url(#waveGradProveedores)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>

          <button className="menu-item menu-item--first" ref={sec1Ref} onClick={handleAddNew}>
            <span className="add-btn-plus">+</span>
            <span className="add-btn-label">AGREGAR PROVEEDOR</span>
          </button>

          <div className="menu-item menu-item--search" ref={sec2Ref}>
            <input
              type="text"
              className="search-input"
              placeholder="Buscar proveedor..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <div className="menu-item menu-item--views" ref={sec3Ref}>
            <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Vista de tarjetas"><FiGrid /></button>
            <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Vista de lista"><FiList /></button>
            <button className={`view-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')} title="Vista de tabla"><FiColumns /></button>
          </div>

          <div className="menu-item menu-item--checkbox" ref={sec4Ref}>
            <div className="filter-dropdown-wrapper" ref={filterDropdownRef}>
              <button
                className={`view-btn ${hayFiltrosActivos ? 'active' : ''}`}
                onClick={() => setShowFilterDropdown(prev => !prev)}
                title="Filtrar proveedores"
              >
                <FiFilter />
              </button>
              {showFilterDropdown && (
                <div className="filter-popover">
                  <div style={{ padding: '6px 16px 4px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px' }}>ESTADO</div>
                  {/* Estados reales del backend (SupplierStatus): SUSPENDED no existe */}
                  {[
                    { id: 'ALL',      label: 'Todos'     },
                    { id: 'ACTIVE',   label: 'Activo'    },
                    { id: 'INACTIVE', label: 'Inactivo'  },
                    { id: 'PENDING',  label: 'Pendiente' },
                    { id: 'BLOCKED',  label: 'Bloqueado' },
                  ].map(op => (
                    <button key={op.id} className={`filter-popover-item ${filterEstado === op.id ? 'active' : ''}`} onClick={() => setFilterEstado(op.id)}>
                      {filterEstado === op.id && <FiCheck />} {op.label}
                    </button>
                  ))}

                  <div style={{ padding: '6px 16px 4px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', borderTop: '1px solid #f1f5f9', marginTop: '4px' }}>CRÉDITO</div>
                  {[
                    { id: 'ALL',          label: 'Todos'        },
                    { id: 'CON_CREDITO',  label: 'Con crédito'  },
                    { id: 'SIN_CREDITO',  label: 'Sin crédito'  },
                  ].map(op => (
                    <button key={op.id} className={`filter-popover-item ${filterCredito === op.id ? 'active' : ''}`} onClick={() => setFilterCredito(op.id)}>
                      {filterCredito === op.id && <FiCheck />} {op.label}
                    </button>
                  ))}

                  <div style={{ padding: '6px 16px 4px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', borderTop: '1px solid #f1f5f9', marginTop: '4px' }}>PAÍS</div>
                  {paisesUnicos.map(pais => (
                    <button key={pais} className={`filter-popover-item ${filterPais === pais ? 'active' : ''}`} onClick={() => setFilterPais(pais)}>
                      {filterPais === pais && <FiCheck />} {pais === 'ALL' ? 'Todos los países' : pais}
                    </button>
                  ))}

                  {hayFiltrosActivos && (
                    <button
                      className="filter-popover-item"
                      style={{ borderTop: '1px solid #f1f5f9', marginTop: '4px', color: '#7B2D8E', fontWeight: 700 }}
                      onClick={() => { setFilterEstado('ALL'); setFilterPais('ALL'); setFilterCredito('ALL'); setShowFilterDropdown(false); }}
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
              )}
            </div>
            <button className="view-btn" onClick={handleDownload} title="Descargar reporte">
              <FiDownload />
            </button>
          </div>
        </div>
      </div>

      {/* Contenido */}
      {proveedoresFiltrados.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiTruck size={48} />
          </div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron proveedores' : 'No hay proveedores registrados'}
          </h3>
          <p className="empty-state-description">
            {searchQuery 
              ? `No se encontraron proveedores que coincidan con "${searchQuery}"`
              : 'Comienza agregando tu primer proveedor al sistema'
            }
          </p>
          {searchQuery && (
            <p>
              <button 
                className="btn btn--ghost" 
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-container">
          <div className="table-scroll">
            <table className="data-table data-table--proveedores">
              <thead>
                <tr>
                  <th>NOMBRE</th>
                  <th>TELÉFONO</th>
                  <th className="text-center">PRODUCTOS</th>
                  <th className="text-center">ÓRDENES</th>
                  <th className="text-center">CRÉDITO</th>
                  <th className="text-center">ESTADO</th>
                  <th className="text-center">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {proveedoresFiltrados.map((proveedor) => (
                  <tr
                    key={proveedor.id}
                    onClick={() => navigate(`/system/proveedores/${proveedor.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{proveedor.nombre}</strong></td>
                    <td>{proveedor.telefono || '-'}</td>
                    <td className="text-center">
                      <span className="badge badge-info">{proveedor.productosActivos || 0}</span>
                    </td>
                    <td className="text-center">{proveedor.ordenesCompra || 0}</td>
                    <td className="text-center">
                      {proveedor.diasCredito > 0
                        ? <span className="badge badge-success">{proveedor.diasCredito} días</span>
                        : '-'}
                    </td>
                    <td className="text-center">
                      <span className={`badge badge-${proveedor.estado === 'ACTIVE' ? 'success' : 'warning'}`}>
                        {getStatusLabel(proveedor.estado)}
                      </span>
                    </td>
                    <td className="text-center" onClick={e => e.stopPropagation()}>
                      <button
                        className="btn-table-delete"
                        onClick={e => handleDeleteProveedor(e, proveedor.id)}
                        title="Desactivar proveedor"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
          {proveedoresFiltrados.map((proveedor) => (
            <div
              key={proveedor.id}
              className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
              onClick={() => navigate(`/system/proveedores/${proveedor.id}`)}
              style={{ cursor: 'pointer' }}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className={`content-thumbnail ${getThumbnailType(proveedor.pais, proveedor.estado)}`}>
                    <span className="logo-text">
                      {proveedor.nombre.split(' ').map(word => word.charAt(0)).join('').substring(0, 4).toUpperCase()}
                    </span>
                    
                    {/* Indicador de estado */}
                    {proveedor.estado !== 'ACTIVE' && (
                      <div className="status-badge status-badge--warning">
                        {getStatusLabel(proveedor.estado)}
                      </div>
                    )}
                  </div>
                  <div className="content-info">
                    <div className="content-title">{proveedor.nombre}</div>
                    <div className="content-subtitle">{proveedor.contactoPrincipal}</div>
                    <div className="content-details">
                      <div className="location-info">
                        <span>{proveedor.ciudad ? `${proveedor.ciudad}, ` : ''}{proveedor.pais}</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`content-thumbnail-small ${getThumbnailType(proveedor.pais, proveedor.estado)}`}>
                    <span className="logo-text-small">
                      {proveedor.nombre.split(' ').map(word => word.charAt(0)).join('').substring(0, 3).toUpperCase()}
                    </span>
                  </div>
                  <div className="content-info-list">
                    <div className="content-title">{proveedor.nombre}</div>
                    <div className="content-subtitle">{proveedor.contactoPrincipal}</div>
                    <div className="content-meta">
                      <span className="meta-item">
                        {proveedor.ciudad ? `${proveedor.ciudad}, ` : ''}{proveedor.pais}
                      </span>
                      <span className="meta-item">Tel: {proveedor.telefono}</span>
                      <span className="meta-item">{getStatusLabel(proveedor.estado)}</span>
                      {proveedor.diasCredito > 0 && (
                        <span className="meta-item info">Crédito: {proveedor.diasCredito} días</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de descarga de reportes */}
      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        title="Descargar Reporte de Proveedores"
        moduleName="proveedores"
        isLoading={isDownloading}
      />
    </div>
  );
};

export default Proveedores;